import React, { useMemo } from 'react';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { BarChart, ChartContainer, Label, Text } from 'components';
import { theme } from 'design';
import { lineHeight, toRem } from 'design/utils';
import { IPayoutStateByDate } from 'types';
import { calculatePayoutStatTotals, centsToDollarsDisplay, paymentStatToChartPoints, statsByDateWithoutCanceled } from 'utils';

const PayoutChartContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(theme.space['2xl'])};
  width: 100%;

  @media (min-width: ${theme.breakpoint.med}px) {
    border-radius: ${toRem(8)};
    border: ${toRem(1)} solid ${theme.color.neutral[200]};
    padding: ${toRem(theme.space['2xl'])};
  }
`;

const PayoutTotals = styled.div`
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: ${toRem(theme.space.md)};
`;

const TotalSection = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  gap: ${toRem(theme.space.md)};

  @media (min-width: ${theme.breakpoint.med}px) {
    flex-flow: row nowrap;
  }
`;

const TotalValue = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  align-items: center;
  gap: ${toRem(theme.space.xs)};

  label {
    font-size: ${toRem(theme.font.size.xs)};
    line-height: ${lineHeight(theme.font.size.xs)};
    color: ${theme.color.neutral[500]};
  }

  p {
    font-size: ${toRem(theme.font.size.lg)};
    line-height: ${lineHeight(theme.font.size.lg)};
    font-weight: ${theme.font.weight.semiBold};
  }
`;

interface IEmployeePayoutChartProps {
  payoutStatsByDate: IPayoutStateByDate[];
  isLoading: boolean;
}

const EmployeePayoutChart = ({ payoutStatsByDate, isLoading }: IEmployeePayoutChartProps) => {
  const { chartData, sourceTotals, disbursementTotals } = useMemo(() => {
    const totalsWithoutCanceled = statsByDateWithoutCanceled(payoutStatsByDate);
    const calculatedStatTotals = calculatePayoutStatTotals(totalsWithoutCanceled);
    return {
      chartData: paymentStatToChartPoints(totalsWithoutCanceled),
      sourceTotals: [
        { label: 'Total', value: calculatedStatTotals.net },
        { label: 'Tips', value: calculatedStatTotals.net - calculatedStatTotals.autoGratuity },
        { label: 'Autograts', value: calculatedStatTotals.autoGratuity },
      ],
      disbursementTotals: [
        { label: 'Payroll', value: calculatedStatTotals.payroll },
        { label: 'Cash', value: calculatedStatTotals.cash },
        { label: 'Instant', value: calculatedStatTotals.instant },
      ],
    };
  }, [payoutStatsByDate]);

  return (
    <PayoutChartContainer data-cy="employee-payout-chart">
      <ChartContainer>
        <BarChart isLoading={isLoading} data={chartData} />
      </ChartContainer>
      <PayoutTotals data-cy="employee-payout-chart-totals">
        {isLoading ? (
          <>
            <TotalSection data-cy="employee-payout-chart-source-totals">
              {Array.from({ length: 3 }).map((_, index) => (
                <TotalValue key={index}>
                  <Skeleton width={toRem(50)} />
                  <Skeleton width={toRem(100)} height={toRem(20)} />
                </TotalValue>
              ))}
            </TotalSection>
            <TotalSection data-cy="employee-payout-chart-disbursement-totals">
              {Array.from({ length: 3 }).map((_, index) => (
                <TotalValue key={index}>
                  <Skeleton width={toRem(50)} />
                  <Skeleton width={toRem(100)} height={toRem(20)} />
                </TotalValue>
              ))}
            </TotalSection>
          </>
        ) : (
          <>
            <TotalSection data-cy="employee-payout-chart-source-totals">
              {sourceTotals.map((source) => (
                <TotalValue key={source.label} data-cy={`employee-payout-chart-total`}>
                  <Label>{source.label}</Label>
                  <Text size="small">{centsToDollarsDisplay(source.value)}</Text>
                </TotalValue>
              ))}
            </TotalSection>
            <TotalSection data-cy="employee-payout-chart-disbursement-totals">
              {disbursementTotals.map((disbursement) => (
                <TotalValue key={disbursement.label} data-cy={`employee-payout-chart-total`}>
                  <Label>{disbursement.label}</Label>
                  <Text size="small">{centsToDollarsDisplay(disbursement.value)}</Text>
                </TotalValue>
              ))}
            </TotalSection>
          </>
        )}
      </PayoutTotals>
    </PayoutChartContainer>
  );
};

export default EmployeePayoutChart;
